import { supabase } from '../lib/supabase';
import { fetchAllRows } from '../lib/fetchAllRows';

export interface UserCoins {
  id: string;
  user_id: string;
  balance: number;
  created_at: string;
  updated_at: string | null;
}

export interface CoinTransaction {
  id: string;
  user_id: string;
  amount: number;
  transaction_type: 'purchase' | 'reward' | 'tournament_entry' | 'tournament_win' | 'admin_gift' | 'admin_deduction' | 'performance_scoring' | 'subscription' | 'referral_gift' | 'referral_subscriber_bonus';
  description: string | null;
  reference_id: string | null;
  admin_id: string | null;
  created_at: string;
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  billing_interval: 'weekly' | 'monthly' | 'annual';
  price_cents: number;
  token_amount: number;
  stripe_product_id: string;
  stripe_price_id: string;
  tag: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string | null;
  plan_type: string;
  status: 'active' | 'cancelled' | 'expired' | 'pending';
  platform: string;
  stripe_subscription_id: string | null;
  started_at: string | null;
  expires_at: string | null;
  cancelled_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface TokenBatch {
  id: string;
  user_id: string;
  subscription_id: string;
  token_amount: number;
  remaining_tokens: number;
  granted_at: string;
  expires_at: string;
  created_at: string;
}

export interface CoinGift {
  id: string;
  user_id: string;
  amount: number;
  admin_id: string;
  gift_type: 'gift' | 'reload' | 'promotion' | 'compensation';
  note: string | null;
  created_at: string;
}

export interface CoinAnalytics {
  total_coins_in_circulation: number;
  users_with_coins: number;
  total_coins_gifted: number;
  total_coins_consumed: number;
  average_user_balance: number;
  transactions_last_24h: number;
  gifts_last_7_days: number;
}

export interface UserCoinStats {
  user_id: string;
  balance: number;
  total_earned: number;
  total_spent: number;
  total_transactions: number;
}

export interface CoinUsageByType {
  transaction_type: string;
  total_amount: number;
  transaction_count: number;
}

export class VirtualCurrencyService {
  async getUserCoins(userId: string): Promise<UserCoins | null> {
    try {
      // Check if user has a coin record
      const { data, error } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Create user coins record if it doesn't exist
          const { data: newData, error: insertError } = await supabase
            .from('user_coins')
            .insert([{ user_id: userId, balance: 0 }])
            .select()
            .single();

          if (insertError) throw insertError;
          return newData;
        } else {
          throw error;
        }
      }
      
      return data;
    } catch (error) {
      console.error('Error getting user coins:', error);
      throw error;
    }
  }

  async addCoins(userId: string, amount: number, type: CoinTransaction['transaction_type'], description?: string, referenceId?: string): Promise<UserCoins> {
    try {
      // Start a transaction
      const { data: userData, error: userError } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (userError) {
        if (userError.code === 'PGRST116') {
          // Create user coins record if it doesn't exist
          const { data: newData, error: insertError } = await supabase
            .from('user_coins')
            .insert([{ user_id: userId, balance: amount }])
            .select()
            .single();

          if (insertError) throw insertError;
          
          // Record transaction
          await this.recordTransaction(userId, amount, type, description, referenceId);
          
          return newData;
        } else {
          throw userError;
        }
      }

      // Update user's balance
      const newBalance = (userData.balance || 0) + amount;
      const { data: updatedData, error: updateError } = await supabase
        .from('user_coins')
        .update({ balance: newBalance })
        .eq('id', userData.id)
        .select()
        .single();

      if (updateError) throw updateError;
      
      // Record transaction
      await this.recordTransaction(userId, amount, type, description, referenceId);
      
      return updatedData;
    } catch (error) {
      console.error('Error adding coins:', error);
      throw error;
    }
  }

  async deductCoins(userId: string, amount: number, type: CoinTransaction['transaction_type'], description?: string, referenceId?: string): Promise<UserCoins> {
    try {
      // Check if user has enough coins
      const { data: userData, error: userError } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (userError) throw userError;

      if ((userData.balance || 0) < amount) {
        throw new Error('Insufficient coins');
      }

      // Update user's balance
      const newBalance = userData.balance - amount;
      const { data: updatedData, error: updateError } = await supabase
        .from('user_coins')
        .update({ balance: newBalance })
        .eq('id', userData.id)
        .select()
        .single();

      if (updateError) throw updateError;
      
      // Record transaction (negative amount)
      await this.recordTransaction(userId, -amount, type, description, referenceId);
      
      return updatedData;
    } catch (error) {
      console.error('Error deducting coins:', error);
      throw error;
    }
  }

  private async recordTransaction(
    userId: string,
    amount: number,
    type: CoinTransaction['transaction_type'],
    description?: string,
    referenceId?: string,
    adminId?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('coin_transactions')
        .insert([{
          user_id: userId,
          amount,
          transaction_type: type,
          description: description || null,
          reference_id: referenceId || null,
          admin_id: adminId || null
        }]);

      if (error) throw error;
    } catch (error) {
      console.error('Error recording transaction:', error);
      throw error;
    }
  }

  async getTransactionHistory(userId: string): Promise<CoinTransaction[]> {
    try {
      return await fetchAllRows<CoinTransaction>(() =>
        supabase
          .from('coin_transactions')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
      );
    } catch (error) {
      console.error('Error getting transaction history:', error);
      throw error;
    }
  }

  async giftCoins(
    userId: string,
    amount: number,
    adminId: string,
    giftType: CoinGift['gift_type'] = 'gift',
    note?: string
  ): Promise<CoinGift> {
    try {
      if (amount <= 0) {
        throw new Error('Gift amount must be positive');
      }

      const { data, error } = await supabase
        .from('coin_gifts')
        .insert([{
          user_id: userId,
          amount,
          admin_id: adminId,
          gift_type: giftType,
          note: note || null
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error gifting coins:', error);
      throw error;
    }
  }

  async getAllUserCoins(limit: number = 100, offset: number = 0): Promise<{ data: UserCoins[], count: number }> {
    try {
      const { data, error, count } = await supabase
        .from('user_coins')
        .select('*', { count: 'exact' })
        .order('balance', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      return { data: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error getting all user coins:', error);
      throw error;
    }
  }

  async getAllTransactions(filters?: {
    userId?: string;
    transactionType?: CoinTransaction['transaction_type'];
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: CoinTransaction[], count: number }> {
    try {
      let query = supabase
        .from('coin_transactions')
        .select('*', { count: 'exact' });

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }

      if (filters?.transactionType) {
        query = query.eq('transaction_type', filters.transactionType);
      }

      if (filters?.startDate) {
        query = query.gte('created_at', filters.startDate);
      }

      if (filters?.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      query = query.order('created_at', { ascending: false });

      if (filters?.limit && filters?.offset !== undefined) {
        query = query.range(filters.offset, filters.offset + filters.limit - 1);
      }

      const { data, error, count } = await query;

      if (error) throw error;
      return { data: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error getting all transactions:', error);
      throw error;
    }
  }

  async getGlobalCoinStats(): Promise<CoinAnalytics> {
    try {
      const { data, error } = await supabase
        .from('coin_analytics')
        .select('*')
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting global coin stats:', error);
      throw error;
    }
  }

  async getUserCoinStats(userId: string): Promise<UserCoinStats> {
    try {
      const { data: coins, error: coinsError } = await supabase
        .from('user_coins')
        .select('balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (coinsError) throw coinsError;

      const transactions = await fetchAllRows<{ amount: number }>(() =>
        supabase
          .from('coin_transactions')
          .select('amount')
          .eq('user_id', userId)
      );

      const total_earned = transactions
        .filter(t => t.amount > 0)
        .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0);

      const total_spent = Math.abs(
        transactions
          .filter(t => t.amount < 0)
          .reduce((sum, t) => sum + parseFloat(t.amount.toString()), 0)
      );

      return {
        user_id: userId,
        balance: coins?.balance || 0,
        total_earned,
        total_spent,
        total_transactions: transactions.length
      };
    } catch (error) {
      console.error('Error getting user coin stats:', error);
      throw error;
    }
  }

  async getCoinUsageByType(): Promise<CoinUsageByType[]> {
    try {
      const data = await fetchAllRows<{ transaction_type: string; amount: number }>(() =>
        supabase
          .from('coin_transactions')
          .select('transaction_type, amount')
          .lt('amount', 0)
      );

      const grouped = data.reduce((acc, tx) => {
        const type = tx.transaction_type;
        if (!acc[type]) {
          acc[type] = { transaction_type: type, total_amount: 0, transaction_count: 0 };
        }
        acc[type].total_amount += Math.abs(parseFloat(tx.amount.toString()));
        acc[type].transaction_count += 1;
        return acc;
      }, {} as Record<string, CoinUsageByType>);

      return Object.values(grouped);
    } catch (error) {
      console.error('Error getting coin usage by type:', error);
      throw error;
    }
  }

  async getCoinGiftHistory(adminId?: string, limit: number = 100, offset: number = 0): Promise<{ data: CoinGift[], count: number }> {
    try {
      let query = supabase
        .from('coin_gifts')
        .select('*', { count: 'exact' });

      if (adminId) {
        query = query.eq('admin_id', adminId);
      }

      query = query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;
      return { data: data || [], count: count || 0 };
    } catch (error) {
      console.error('Error getting coin gift history:', error);
      throw error;
    }
  }

  async exportTransactionsToCSV(filters?: {
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<string> {
    try {
      const { data } = await this.getAllTransactions({
        ...filters,
        limit: 10000
      });

      const headers = ['ID', 'User ID', 'Amount', 'Type', 'Description', 'Reference ID', 'Admin ID', 'Created At'];
      const rows = data.map(tx => [
        tx.id,
        tx.user_id,
        tx.amount,
        tx.transaction_type,
        tx.description || '',
        tx.reference_id || '',
        tx.admin_id || '',
        tx.created_at
      ]);

      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      return csv;
    } catch (error) {
      console.error('Error exporting transactions to CSV:', error);
      throw error;
    }
  }

  async adminDeductCoins(
    userId: string,
    amount: number,
    adminId: string,
    reason?: string
  ): Promise<UserCoins> {
    try {
      const { data: userData, error: userError } = await supabase
        .from('user_coins')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (userError) throw userError;

      if ((userData.balance || 0) < amount) {
        throw new Error('Insufficient coins');
      }

      const newBalance = userData.balance - amount;
      const { data: updatedData, error: updateError } = await supabase
        .from('user_coins')
        .update({ balance: newBalance })
        .eq('id', userData.id)
        .select()
        .single();

      if (updateError) throw updateError;

      await this.recordTransaction(
        userId,
        -amount,
        'admin_deduction',
        reason || 'Admin deduction',
        undefined,
        adminId
      );

      return updatedData;
    } catch (error) {
      console.error('Error admin deducting coins:', error);
      throw error;
    }
  }

  async getActiveSubscription(userId: string): Promise<UserSubscription | null> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting active subscription:', error);
      throw error;
    }
  }

  async getUserTokenBatches(userId: string): Promise<TokenBatch[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_token_batches')
        .select('*')
        .eq('user_id', userId)
        .gt('remaining_tokens', 0)
        .order('expires_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting token batches:', error);
      throw error;
    }
  }

  async getAllTokenBatches(userId: string): Promise<TokenBatch[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_token_batches')
        .select('*')
        .eq('user_id', userId)
        .order('granted_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all token batches:', error);
      throw error;
    }
  }

  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting subscription plans:', error);
      throw error;
    }
  }

  async getAllSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting all subscription plans:', error);
      throw error;
    }
  }

  async getAllSubscriptions(): Promise<(UserSubscription & { username?: string; full_name?: string })[]> {
    try {
      const { data: subs, error } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!subs || subs.length === 0) return [];

      const userIds = [...new Set(subs.map(s => s.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, full_name')
        .in('id', userIds);

      return subs.map(sub => ({
        ...sub,
        username: profiles?.find(p => p.id === sub.user_id)?.username,
        full_name: profiles?.find(p => p.id === sub.user_id)?.full_name,
      }));
    } catch (error) {
      console.error('Error getting all subscriptions:', error);
      throw error;
    }
  }
}